<template>
  <div
    class="event-data-at-a-glance"
  >
    <v-card v-if="!loaded" class="event-data-at-a-glance__card">
      <ContentLoader viewBox="0 0 100 100">
        <rect x="0" y="0" rx="3" ry="3" width="250" height="10" />
        <rect x="20" y="20" rx="3" ry="3" width="220" height="10" />
        <rect x="20" y="40" rx="3" ry="3" width="170" height="10" />
        <rect x="0" y="60" rx="3" ry="3" width="250" height="10" />
        <rect x="20" y="80" rx="3" ry="3" width="200" height="10" />
        <rect x="20" y="100" rx="3" ry="3" width="80" height="10" />
      </ContentLoader>
    </v-card>

    <v-card v-if="orgData && orgData.apps && organisationDashboardDates && loaded === true">

      <v-card-title >
        <div
          class="d-flex event-data-at-a-glance__title justify-space-between h3"
          :class="{ 'white--text': isDarkMode }"
        >
          Event details at a glance
        </div>
      </v-card-title>
      <v-card-text class="text-left subtitle-1">
        <div class="d-flex" :key="selectedIdx">
          <DoughnutChart
            class="event-data-at-a-glance__chart"
            v-if="fetchedData"
            :clientHash="clientHash"
            :chartData="fetchedData"
            @on-receive="update"
            @redirectToEvent="redirect($event)"
          />
          <div
            v-if="$vuetify.breakpoint.width <= 768"
            class="d-flex flex-column justify-center"
          >
            <div class="d-flex mb-2">
              <apio-icon
                class="mr-2"
                size="16"
                name="ui-bullet"
                lightModeColour="success"
                darkModeColour="success"
              /><span class="small">Success</span>
            </div>
            <div class="d-flex mb-2">
              <apio-icon
                class="mr-2"
                size="16"
                name="ui-bullet"
                lightModeColour="warning"
                darkModeColour="warning"
              /><span class="small">Warning</span>
            </div>
            <div class="d-flex mb-2">
              <apio-icon
                class="mr-2"
                size="16"
                name="ui-bullet"
                lightModeColour="error"
                darkModeColour="error"
              /><span class="small">Error</span>
            </div>
          </div>
          <div
            v-if="$vuetify.breakpoint.width > 768"
            class="event-data-at-a-glance__chart--legend"
          >
            <div
              class="event-data-at-a-glance__chart--legend-item"
              v-for="(app, idx) in orgData.apps"
              :key="idx"
            >
              <apio-button
                @click="
                  updateChartData(orgData.apps[idx], true);
                  selectedIdx = idx;
                "
                button-size="small"
                :button-type="[selectedIdx === idx ? 'selected' : 'secondary']"
                >{{ app.name }}
                <status-indicator v-if="app.running" class="ml-2" status="positive" pulse></status-indicator>
                </apio-button
              >
            </div>
          </div>
        </div>
        <div
          v-if="$vuetify.breakpoint.width > 768"
          class="event-data-at-a-glance__legend--desktop d-flex justify-end pr-4"
        >
          <div class="d-flex mr-2">
            <apio-icon
              class="mr-2"
              size="16"
              name="ui-bullet"
              lightModeColour="success"
              darkModeColour="success"
            /><span class="small">Success</span>
          </div>
          <div class="d-flex mr-2">
            <apio-icon
              class="mr-2"
              size="16"
              name="ui-bullet"
              lightModeColour="warning"
              darkModeColour="warning"
            /><span class="small">Warning</span>
          </div>
          <div class="d-flex">
            <apio-icon
              class="mr-2"
              size="16"
              name="ui-bullet"
              lightModeColour="error"
              darkModeColour="error"
            /><span class="small">Error</span>
          </div>
        </div>
      </v-card-text>
      <div style="padding: 8px 0" v-if="$vuetify.breakpoint.width < 768">
        <div
          style="padding: 4px 16px"
          v-for="(org, idx) in orgData.apps"
          :key="idx"
        >
          <apio-button
            @click="selectedIdx = idx"
            style="width: 100%"
            button-size="small"
            :button-type="[selectedIdx === idx ? 'selected' : 'secondary']"
            >{{ org.name }}</apio-button
          >
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { StatusIndicator } from "vue-status-indicator";
import request from '@/API/';

export default {
  name: "EventDataAtAGlance",
  components: {
    StatusIndicator,
    DoughnutChart: () => import("@/components/DoughnutChart.vue"),
    // Help: () => import("@/components/Help/Help.vue"),
  },
  props: {
    orgData: {
      type: Object,
      default: () => {},
    },
    clientHash: {
      type: String,
      required: true,
    },
    timeStart: {
      type: [String, Number],
      //   required: true,
    },
    timeEnd: {
      type: [String, Number],
      //   required: true,
    },
    organisation: {
      type: String,
      required: false,
    },
    interfaceMode: {
      default: true,
    },
  },
  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      await vm.$store.dispatch("getApps", {
        c: vm.$route.query.c,
        q: "",
      });
    });
  },
  data() {
    return {
      errorCount: 0,
      selectedIdx: 0,
      successCount: 0,
      warningCount: 0,
      noDataCount: 0,
      loaded: false,
    };
  },
  async mounted() {
    this.selectedIdx = 0;
    await this.updateChartData(this.orgData?.apps[0]);
    this.loaded = true;
  },
  watch: {
    async orgData(newValue) {
      if (newValue) {
        await this.updateChartData(this.orgData.apps[0]);
      }
    },
  },
  computed: {
    ...mapGetters(["organisationDashboardDates"]),
    ...mapState({
      organisationDashboard: (state) => state.orgs.organisationDashboard,
      organisationDashboardDates: (state) =>
        state.orgs.organisationDashboardDates,
    }),
    fetchedData() {
      let noDataCount = 0;
      if (
        this.successCount === 0 &&
        this.errorCount === 0 &&
        this.warningCount === 0
      ) {
        noDataCount = 1;
      }

      return {
        labels: ["Successes", "Warnings", "Errors", "No Data"],
        borderWidth: 1,
        datasets: [
          {
            backgroundColor: ["#61bb46", "#fdb827", "#e03a3e", "#cccccc"],
            borderColor: ["#61bb46", "#fdb827", "#e03a3e", "#cccccc"],
            data: [
              this.successCount,
              this.warningCount,
              this.errorCount,
              noDataCount,
            ],
          },
        ],
      };
    },
    selectedApp() {
      return this.orgData && this.orgData.apps
        ? this.orgData.apps[this.selectedIdx]
        : this.appId;
    },
    isDarkMode() {
      return this.$vuetify.theme.dark;
    },
  },
  methods: {
    async updateChartData(idx, fromChart = false) {
      this.orgData.apps.forEach(app => {
      request
        .get("/ingest/list/" + app.id)
        .then((res) => {
          if (
            Object.keys(res.data).length === 0 &&
            res.data.constructor === Object
          ) {
            return;
          }

          for (const [key, value] of Object.entries(res.data)) {
            console.log(key,value);
            app.running = true;
          }
        })
        .catch((err) => {
          this.error = err;
        });
      });
      if (!idx) return;
      this.successCount = idx.count || 0;
      this.errorCount = idx.error || 0;
      this.warningCount = idx.warning || 0;
      this.selectIdx = idx;
      if(fromChart === true) {
        this.$emit('selectApp', idx.id)
      }
    },
    update() {
      this.$router.push(
        `/events-log?f=${this.organisationDashboardDates.startDate}&t=${this.organisationDashboardDates.endDate}&c=${this.$route.query.c}&a=${this.selectedApp.id}`
      );
    },
    redirect(label) {
      if (this.interfaceMode) {
        if (label === "Errors") {
          this.$router.push(
            `/interface-events?f=${this.organisationDashboardDates.startDate}&t=${this.organisationDashboardDates.endDate}&q=error&c=${this.hashes[0]}&a=${this.hashes[1]}&i=${this.hashes[2]}`
          );
        }
        this.$router.push(
          `/interface-events?f=${this.organisationDashboardDates.startDate}&t=${this.organisationDashboardDates.endDate}&q=success&c=${this.hashes[0]}&a=${this.hashes[1]}&i=${this.hashes[2]}`
        );
      } else {
        if (label === "Errors") {
          this.$router.push(
            `/organisation-dashboard?f=${this.organisationDashboardDates.startDate}&t=${this.organisationDashboardDates.endDate}&q=error&c=${this.hashes[0]}&a=${this.hashes[1]}&i=${this.hashes[2]}`
          );
        }
        this.$router.push(
          `/organisation-dashboard?f=${this.organisationDashboardDates.startDate}&t=${this.organisationDashboardDates.endDate}&c=${this.hashes[0]}&a=${this.hashes[1]}&i=${this.hashes[2]}`
        );
      }
    },
  },
};
</script>

<style lang="scss" src="./EventDataAtAGlance.scss" scoped/>
